import type {
  CreateProjectTaskOutputRequest,
  ProjectListItem,
  ProjectTaskOutput,
  ProjectTaskOutputWithTaskName,
  ProjectTaskRevision,
  Team,
  TeamMemberWithUser,
  UpdateProjectTaskOutputRequest,
} from '@doneverse/shared'
import { api } from '../api/index.js'

export const teamService = {
  fetchTeamsForUser: async (): Promise<Team[]> => {
    try {
      return await api.get('/v1/teams').json<Team[]>()
    } catch {
      console.warn('Failed to fetch teams from /v1/teams')
      return []
    }
  },

  fetchTeamById: async (teamId: string): Promise<Team | null> => {
    try {
      return await api.get(`/v1/teams/${teamId}`).json<Team>()
    } catch {
      return null
    }
  },

  fetchTeamMembersForTeam: async (teamId: string): Promise<TeamMemberWithUser[]> => {
    try {
      return await api.get(`/v1/teams/${teamId}/members`).json<TeamMemberWithUser[]>()
    } catch {
      console.warn('Failed to fetch team members for team', teamId)
      return []
    }
  },

  assignProjectMember: async (projectId: string, memberId: string) => {
    // TODO: Need PATCH /v1/projects/:id endpoint
    await api
      .patch(
        {
          doing_team_member_id: memberId,
        },
        `/v1/projects/${projectId}`,
      )
      .json()
  },

  unassignProjectMember: async (projectId: string) => {
    // TODO: Need PATCH /v1/projects/:id endpoint
    await api
      .patch(
        {
          doing_team_member_id: null,
        },
        `/v1/projects/${projectId}`,
      )
      .json()
  },

  fetchAssignedProjectsDetailed: async (): Promise<ProjectListItem[]> => {
    return api.get('/v1/projects').json<ProjectListItem[]>()
  },

  fetchProjectById: async (projectId: string): Promise<ProjectListItem> => {
    return api.get(`/v1/projects/${projectId}`).json<ProjectListItem>()
  },

  fetchProjectsForTeams: async (_teamIds: string[]): Promise<ProjectListItem[]> => {
    // The API already resolves team membership via project_tasks —
    // no client-side filter needed.
    return api.get('/v1/projects').json<ProjectListItem[]>()
  },

  fetchProjectOutputs: async (projectId: string): Promise<ProjectTaskOutputWithTaskName[]> => {
    return api.get(`/v1/projects/${projectId}/output`).json<ProjectTaskOutputWithTaskName[]>()
  },

  submitProjectOutput: async (
    projectId: string,
    output: CreateProjectTaskOutputRequest,
  ): Promise<ProjectTaskOutput> => {
    return api.post(output, `/v1/projects/${projectId}/output`).json<ProjectTaskOutput>()
  },

  updateProjectOutput: async (
    projectId: string,
    output: UpdateProjectTaskOutputRequest,
  ): Promise<ProjectTaskOutput> => {
    return api.patch(output, `/v1/projects/${projectId}/output`).json<ProjectTaskOutput>()
  },

  submitToFounder: async (projectId: string): Promise<{ success: boolean; action: string }> => {
    return api.patch({ action: 'submit_to_founder' }, `/v1/projects/${projectId}`).json()
  },

  requestDtRevision: async (
    projectId: string,
    notes: string,
  ): Promise<{ success: boolean; action: string }> => {
    return api.patch({ action: 'request_dt_revision', notes }, `/v1/projects/${projectId}`).json()
  },

  fetchProjectTaskRevisions: async (projectId: string, taskId: string): Promise<ProjectTaskRevision[]> => {
    return api.get(`/v1/projects/${projectId}/tasks/${taskId}/revisions`).json<ProjectTaskRevision[]>()
  },

  completeProjectTask: async (
    projectId: string,
    taskId: string,
  ): Promise<{ success: boolean; action: string; task_id: string }> => {
    return api.patch({ action: 'complete' }, `/v1/projects/${projectId}/tasks/${taskId}`).json()
  },

  markProjectRead: async (projectId: string): Promise<void> => {
    await api.post({}, `/v1/projects/${projectId}/read`).res()
  },
}
